import { useEffect, useState } from "react";
import { createFileRoute } from '@tanstack/react-router' 
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Trash2, X, Check, GripVertical, Edit } from "lucide-react";
import { TodoEditModal } from "@/components/todo/TodoEditModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { API_ADMIN_BASE_URL } from "@/lib/api-client";

interface TodoItem {
  id: string;
  feature: string;
  description?: string;
  frontendTitle: string;
  frontendPercent: number;
  backendTitle: string;
  backendPercent: number;
  infrastructureTitle: string;
  infrastructurePercent: number;
  statusPercent: number;
  order: number;
  categoryId: string;
}

interface TodoCategory {
  id: string;
  title: string;
  description: string;
  todos: TodoItem[];
}

const PERCENT_LABELS: Record<number, { label: string; emoji: string; color: string }> = {
  [-1]: { label: "N/A", emoji: "—", color: "text-gray-400" },
  0: { label: "Not Started", emoji: "🔴", color: "text-red-500" },
  25: { label: "Partial", emoji: "🟡", color: "text-orange-500" },
  50: { label: "Functional", emoji: "🟢", color: "text-yellow-500" },
  75: { label: "Almost Done", emoji: "🔵", color: "text-blue-500" },
  100: { label: "Complete", emoji: "✅", color: "text-green-500" },
};

function getPercentLabel(percent: number) {
  // Handle N/A (-1) explicitly
  if (percent === -1) {
    return PERCENT_LABELS[-1];
  }
  return PERCENT_LABELS[percent] || PERCENT_LABELS[0];
}

// Sortable Table Row Component
function SortableTodoRow({
  item,
  isEditing,
  editForm,
  onEdit,
  onEditModal,
  onSave,
  onCancel,
  onDelete,
  onFormChange,
  isDescExpanded,
  onToggleDesc,
}: {
  item: TodoItem;
  isEditing: boolean;
  editForm: Partial<TodoItem>;
  onEdit: () => void;
  onEditModal: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onFormChange: (updates: Partial<TodoItem>) => void;
  isDescExpanded: boolean;
  onToggleDesc: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const statusInfo = getPercentLabel(item.statusPercent);

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        className="hover:bg-muted/50 select-none"
        onDoubleClick={isEditing ? undefined : onEditModal}
      >
        <TableCell className="w-8">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </TableCell>
        <TableCell onClick={onToggleDesc} className="cursor-pointer">
          {isEditing ? (
            <Input
              value={editForm.feature || ""}
              onChange={e => onFormChange({ feature: e.target.value })}
              className="h-8 text-sm"
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="font-medium text-sm">{item.feature}</span>
          )}
        </TableCell>

        {/* Frontend */}
        <TableCell>
          {isEditing ? (
            <div className="space-y-2">
              <Input
                value={editForm.frontendTitle || ""}
                onChange={e => onFormChange({ frontendTitle: e.target.value })}
                placeholder="Status text"
                className="h-7 text-xs"
              />
              <div className="flex items-center gap-2">
                <Slider
                  value={[editForm.frontendPercent ?? 0]}
                  onValueChange={([value]) => onFormChange({ frontendPercent: value })}
                  min={-1}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <span className="text-xs font-mono w-10">{getPercentLabel(editForm.frontendPercent ?? 0).emoji}</span>
              </div>
            </div>
          ) : (
            <div>
              {item.frontendPercent === -1 ? (
                <div className="text-xs flex items-center gap-2">
                  <span className="text-gray-400">N/A</span>
                  <div className="h-2 w-2 rounded-full bg-gray-400" />
                </div>
              ) : (
                <div className="text-xs flex items-center gap-2">
                  <span>{item.frontendTitle}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.frontendPercent}% {getPercentLabel(item.frontendPercent).emoji}
                  </span>
                </div>
              )}
            </div>
          )}
        </TableCell>

        {/* Backend */}
        <TableCell>
          {isEditing ? (
            <div className="space-y-2">
              <Input
                value={editForm.backendTitle || ""}
                onChange={e => onFormChange({ backendTitle: e.target.value })}
                placeholder="Status text"
                className="h-7 text-xs"
              />
              <div className="flex items-center gap-2">
                <Slider
                  value={[editForm.backendPercent ?? 0]}
                  onValueChange={([value]) => onFormChange({ backendPercent: value })}
                  min={-1}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <span className="text-xs font-mono w-10">{getPercentLabel(editForm.backendPercent ?? 0).emoji}</span>
              </div>
            </div>
          ) : (
            <div>
              {item.backendPercent === -1 ? (
                <div className="text-xs flex items-center gap-2">
                  <span className="text-gray-400">N/A</span>
                  <div className="h-2 w-2 rounded-full bg-gray-400" />
                </div>
              ) : (
                <div className="text-xs flex items-center gap-2">
                  <span>{item.backendTitle}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.backendPercent}% {getPercentLabel(item.backendPercent).emoji}
                  </span>
                </div>
              )}
            </div>
          )}
        </TableCell>

        {/* Infrastructure */}
        <TableCell>
          {isEditing ? (
            <div className="space-y-2">
              <Input
                value={editForm.infrastructureTitle || ""}
                onChange={e => onFormChange({ infrastructureTitle: e.target.value })}
                placeholder="Status text"
                className="h-7 text-xs"
              />
              <div className="flex items-center gap-2">
                <Slider
                  value={[editForm.infrastructurePercent ?? 0]}
                  onValueChange={([value]) => onFormChange({ infrastructurePercent: value })}
                  min={-1}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <span className="text-xs font-mono w-10">
                  {getPercentLabel(editForm.infrastructurePercent ?? 0).emoji}
                </span>
              </div>
            </div>
          ) : (
            <div>
              {item.infrastructurePercent === -1 ? (
                <div className="text-xs flex items-center gap-2">
                  <span className="text-gray-400">N/A</span>
                  <div className="h-2 w-2 rounded-full bg-gray-400" />
                </div>
              ) : (
                <div className="text-xs flex items-center gap-2">
                  <span>{item.infrastructureTitle}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.infrastructurePercent}% {getPercentLabel(item.infrastructurePercent).emoji}
                  </span>
                </div>
              )}
            </div>
          )}
        </TableCell>

        {/* Status */}
        <TableCell>
          <div className={`text-xs font-medium ${statusInfo.color}`}>
            {statusInfo.emoji} {item.statusPercent}%
          </div>
        </TableCell>

        {/* Actions */}
        <TableCell>
          {isEditing ? (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={onSave} className="h-7 px-2">
                <Check className="h-3 w-3 text-green-500" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 px-2">
                <X className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} className="text-destructive h-7 px-2">
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2 text-xs">
                Quick Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={onEditModal} className="h-7 px-2">
                <Edit className="h-3 w-3" />
              </Button>
            </div>
          )}
        </TableCell>
      </TableRow>

      {/* Description Row - Collapsible */}
      {item.description && isDescExpanded && (
        <TableRow>
          <TableCell colSpan={7} className="py-2 px-4 bg-muted/30">
            <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap">{item.description}</pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export const Route = createFileRoute('/dashboard/todo/$categoryId')({
  component: CategoryDetailPage,
})

function CategoryDetailPage() {
  const { categoryId } = Route.useParams();

  const [category, setCategory] = useState<TodoCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TodoItem>>({});

  // Modal state
  const [modalItem, setModalItem] = useState<TodoItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Description toggle state (for category description)
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  // Todo item description toggle state (map of item.id -> boolean)
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // DND sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (categoryId) {
      fetchCategory();
    }
  }, [categoryId, fetchCategory]);

  async function fetchCategory() {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/categories/${categoryId}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setCategory(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch category:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreate() {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          categoryId,
          feature: "New Feature",
          frontendTitle: "",
          frontendPercent: 0,
          backendTitle: "",
          backendPercent: 0,
          infrastructureTitle: "",
          infrastructurePercent: 0,
        }),
      });

      const data = await res.json();
      if (data.success) {
        fetchCategory();
      }
    } catch (error) {
      console.error("Failed to create item:", error);
    }
  }

  async function handleUpdate(id: string) {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/items/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editForm),
      });

      const data = await res.json();
      if (data.success) {
        setEditingId(null);
        setEditForm({});
        fetchCategory();
      }
    } catch (error) {
      console.error("Failed to update item:", error);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this item?")) return;

    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/items/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (data.success) {
        fetchCategory();
      }
    } catch (error) {
      console.error("Failed to delete item:", error);
    }
  }

  function startEdit(item: TodoItem) {
    setEditingId(item.id);
    setEditForm(item);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  function openModal(item: TodoItem) {
    setModalItem(item);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setModalItem(null);
  }

  async function handleModalSave(updates: Partial<TodoItem>) {
    if (!modalItem) return;

    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/items/${modalItem.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });

      const data = await res.json();
      if (data.success) {
        fetchCategory();
        closeModal();
      }
    } catch (error) {
      console.error("Failed to update item:", error);
      throw error;
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    if (!category) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = category.todos.findIndex(t => t.id === active.id);
    const newIndex = category.todos.findIndex(t => t.id === over.id);

    const reordered = arrayMove(category.todos, oldIndex, newIndex);
    setCategory({ ...category, todos: reordered });

    // Update order in database
    const items = reordered.map((todo, index) => ({
      id: todo.id,
      order: index,
    }));

    try {
      await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/items/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items }),
      });
    } catch (error) {
      console.error("Failed to reorder items:", error);
      fetchCategory();
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Category not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{category.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{category.todos.length} implementation tasks</p>
        </div>
        <Button onClick={handleCreate} size="sm" className="text-xs">
          <Plus className="h-3 w-3 mr-2" />
          Insert Item
        </Button>
      </div>

      {/* Description */}
      {category.description && (
        <Card>
          <CardContent className="pt-4 pb-2 px-6">
            <div className="relative">
              <pre
                className={`font-mono text-xs leading-relaxed whitespace-pre-wrap overflow-hidden transition-all duration-300 ${
                  isDescExpanded ? "max-h-none" : "max-h-[2.8rem]"
                }`}
              >
                {category.description}
              </pre>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDescExpanded(!isDescExpanded)}
                className="text-xs h-5 bg-background px-3 text-muted-foreground hover:text-foreground absolute left-1/2 -translate-x-1/2 bottom-[-20px]"
              >
                {isDescExpanded ? "Less" : "More"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todo Items Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Implementation Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-[200px] text-xs">Feature</TableHead>
                    <TableHead className="w-[300px] text-xs">Frontend</TableHead>
                    <TableHead className="w-[300px] text-xs">Backend</TableHead>
                    <TableHead className="w-[300px] text-xs">Infrastructure</TableHead>
                    <TableHead className="w-[100px] text-xs">Status</TableHead>
                    <TableHead className="w-[100px] text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <SortableContext items={category.todos.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <TableBody>
                    {category.todos.map(item => (
                      <SortableTodoRow
                        key={item.id}
                        item={item}
                        isEditing={editingId === item.id}
                        editForm={editForm}
                        onEdit={() => startEdit(item)}
                        onEditModal={() => openModal(item)}
                        onSave={() => handleUpdate(item.id)}
                        onCancel={cancelEdit}
                        onDelete={() => handleDelete(item.id)}
                        onFormChange={updates => setEditForm({ ...editForm, ...updates })}
                        isDescExpanded={expandedItems[item.id] || false}
                        onToggleDesc={() => setExpandedItems({ ...expandedItems, [item.id]: !expandedItems[item.id] })}
                      />
                    ))}
                  </TableBody>
                </SortableContext>
              </Table>
            </DndContext>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <TodoEditModal item={modalItem} isOpen={isModalOpen} onClose={closeModal} onSave={handleModalSave} />
    </div>
  );
}
